// src/views/PortfolioSimulatorView.js
import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2, Calculator, AlertTriangle, TrendingUp, TrendingDown, Activity, Sliders } from 'lucide-react';
import { API_BASE } from '../api/db';

const SCENARIOS = {
  NORMAL: { label: 'Normal Market', marketShift: 0.0, color: 'text-slate-400', bg: 'bg-slate-800', icon: Activity },
  BULL: { label: 'Bull Rally (+20%)', marketShift: 0.20, color: 'text-emerald-500', bg: 'bg-emerald-500/20', icon: TrendingUp },
  BEAR: { label: 'Bear Market (-20%)', marketShift: -0.20, color: 'text-orange-500', bg: 'bg-orange-500/20', icon: TrendingDown },
  CRASH: { label: 'Flash Crash (-35%)', marketShift: -0.35, color: 'text-red-500', bg: 'bg-red-500/20', icon: AlertTriangle },
  CUSTOM: { label: 'Custom', marketShift: 0, color: 'text-indigo-400', bg: 'bg-indigo-500/20', icon: Sliders }
};

export default function PortfolioSimulatorView({ themeClasses, currentUser }) {
  const [budget, setBudget] = useState(100000);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  const [activeScenario, setActiveScenario] = useState('NORMAL');
  const [customShift, setCustomShift] = useState(-10);

  useEffect(() => {
    if (!currentUser || !currentUser.id) {
        setLoading(false);
        return;
    }
    
    fetch(`${window.location.origin}${API_BASE}/data/${currentUser.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.sim_data) {
          setBudget(data.sim_data.budget || 100000);
          setRows(data.sim_data.rows || []);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [currentUser]);

  const saveToDb = (newBudget, newRows) => {
    if (!currentUser || !currentUser.id) return;
    setSyncing(true);
    fetch(`${window.location.origin}${API_BASE}/data/${currentUser.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sim_data: { budget: newBudget, rows: newRows } })
    }).finally(() => setTimeout(() => setSyncing(false), 500));
  };

  const addRow = () => {
    const safeId = `row-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    const newRows = [...rows, { id: safeId, symbol: '', price: 0, shares: 0, weight: 0, budgetDollars: 0, yieldPct: 0, yieldDollars: 0, beta: 1.0 }];
    setRows(newRows);
    saveToDb(budget, newRows);
  };

  const removeRow = (id) => {
    const newRows = rows.filter(r => r.id !== id);
    setRows(newRows);
    saveToDb(budget, newRows);
  };

  const updateRow = (id, field, value) => {
    const newRows = rows.map(row => {
      if (row.id !== id) return row;
      const updated = { ...row, [field]: value };
      const numVal = parseFloat(value) || 0;

      if (field === 'weight') {
        updated.budgetDollars = budget * (numVal / 100);
        updated.shares = updated.price > 0 ? updated.budgetDollars / updated.price : 0;
      } else if (field === 'budgetDollars') {
        updated.weight = budget > 0 ? (numVal / budget) * 100 : 0;
        updated.weight = Math.round(updated.weight * 10) / 10;
        updated.shares = updated.price > 0 ? numVal / updated.price : 0;
      } else if (field === 'shares' || field === 'price') {
        updated.budgetDollars = updated.shares * updated.price;
        updated.weight = budget > 0 ? (updated.budgetDollars / budget) * 100 : 0;
        updated.weight = Math.round(updated.weight * 10) / 10;
      }

      updated.yieldDollars = updated.budgetDollars * ((parseFloat(updated.yieldPct) || 0) / 100);
      return updated;
    });

    setRows(newRows);
    saveToDb(budget, newRows);
  };

  const handleBudgetChange = (newBudget) => {
    const val = parseFloat(newBudget) || 0;
    setBudget(val);
    const newRows = rows.map(r => {
        r.budgetDollars = val * (parseFloat(r.weight) || 0) / 100;
        r.shares = r.price > 0 ? r.budgetDollars / r.price : 0;
        r.yieldDollars = r.budgetDollars * ((parseFloat(r.yieldPct) || 0) / 100);
        return r;
    });
    setRows(newRows);
    saveToDb(val, newRows);
  };

  const calculateRiskMetrics = async (ticker) => {
    try {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setFullYear(toDate.getFullYear() - 1); 
      
      const toStr = toDate.toISOString().split('T')[0];
      const fromStr = fromDate.toISOString().split('T')[0];
      const baseUrl = `${window.location.origin}${API_BASE}`;

      const [stockRes, spyRes] = await Promise.all([
        fetch(`${baseUrl}/massive/v2/aggs/ticker/${ticker}/range/1/day/${fromStr}/${toStr}?adjusted=true&sort=asc&limit=300`),
        fetch(`${baseUrl}/massive/v2/aggs/ticker/SPY/range/1/day/${fromStr}/${toStr}?adjusted=true&sort=asc&limit=300`)
      ]);

      const stockData = await stockRes.json();
      const spyData = await spyRes.json();

      if (!stockData.results || !spyData.results) return 1.0;

      const spyMap = {};
      spyData.results.forEach(d => { spyMap[d.t] = d.c; });

      const stockReturns = [];
      const spyReturns = [];

      for (let i = 1; i < stockData.results.length; i++) {
        const today = stockData.results[i];
        const yesterday = stockData.results[i - 1];
        if (spyMap[today.t] && spyMap[yesterday.t]) {
          stockReturns.push((today.c - yesterday.c) / yesterday.c);
          spyReturns.push((spyMap[today.t] - spyMap[yesterday.t]) / spyMap[yesterday.t]);
        }
      }

      if (stockReturns.length === 0) return 1.0;

      const meanStock = stockReturns.reduce((a, b) => a + b, 0) / stockReturns.length;
      const meanSpy = spyReturns.reduce((a, b) => a + b, 0) / spyReturns.length;

      let covariance = 0, varianceSpy = 0;
      for (let i = 0; i < stockReturns.length; i++) {
        covariance += (stockReturns[i] - meanStock) * (spyReturns[i] - meanSpy);
        varianceSpy += Math.pow((spyReturns[i] - meanSpy), 2);
      }
      
      return varianceSpy === 0 ? 1.0 : (covariance / varianceSpy);
    } catch (e) {
      console.warn("Sandbox Beta Calculation failed");
      return 1.0;
    }
  };

  const fetchAssetData = async (id, symbol) => {
    if (!symbol) return;
    const baseUrl = `${window.location.origin}${API_BASE}`;
    try {
      const [massiveRes, yfRes, dynamicBeta] = await Promise.all([
        fetch(`${baseUrl}/massive/v2/snapshot/locale/us/markets/stocks/tickers/${symbol}`).then(r => r.json()).catch(() => ({})),
        fetch(`${baseUrl}/company/${symbol}`).then(r => r.json()).catch(() => ({})),
        calculateRiskMetrics(symbol) 
      ]);
      
      let newPrice = 0;
      let newYield = 0;

      if (massiveRes.ticker) {
        const t = massiveRes.ticker;
        newPrice = t.lastTrade?.p || t.day?.c || t.prevDay?.c || 0;
      }
      
      if (yfRes) {
        const yStr = yfRes.secYield !== "N/A" && yfRes.secYield ? yfRes.secYield : yfRes.divYield;
        if (yStr && yStr !== "N/A") {
            newYield = parseFloat(yStr.replace('%', ''));
        }
      }

      const newRows = rows.map(r => {
        if (r.id !== id) return r;
        r.price = newPrice;
        r.yieldPct = newYield;
        r.beta = parseFloat(dynamicBeta.toFixed(2)); 
        r.shares = r.price > 0 ? r.budgetDollars / r.price : 0;
        r.yieldDollars = r.budgetDollars * (r.yieldPct / 100);
        return r;
      });

      setRows(newRows);
      saveToDb(budget, newRows);
    } catch (e) {
      console.warn("Fetch failed for", symbol);
    }
  };

  const totalWeight = rows.reduce((sum, r) => sum + (parseFloat(r.weight) || 0), 0);
  const totalAllocated = rows.reduce((sum, r) => sum + (parseFloat(r.budgetDollars) || 0), 0);
  const totalYieldDollars = rows.reduce((sum, r) => sum + (parseFloat(r.yieldDollars) || 0), 0);
  const blendedYield = totalAllocated > 0 ? (totalYieldDollars / totalAllocated) * 100 : 0;

  const currentScenario = SCENARIOS[activeScenario];
  const isStressed = activeScenario !== 'NORMAL';
  
  let totalStressedAllocated = 0;
  
  const processedRows = rows.map(r => {
      let marketShift = currentScenario.marketShift;
      if (activeScenario === 'CUSTOM') {
          marketShift = (parseFloat(customShift) || 0) / 100;
      }

      const assetShiftPct = marketShift * (parseFloat(r.beta) || 1.0);
      const stressedPrice = r.price * (1 + assetShiftPct);
      const stressedAllocated = r.shares * stressedPrice;
      
      totalStressedAllocated += stressedAllocated;
      
      return {
          ...r,
          stressedPrice,
          stressedAllocated,
          assetShiftPct: assetShiftPct * 100
      };
  });

  const portfolioDrawdown = totalAllocated > 0 ? ((totalStressedAllocated - totalAllocated) / totalAllocated) * 100 : 0;

  if (loading) return <div className="flex justify-center items-center h-full"><Loader2 className="animate-spin text-indigo-500" size={48}/></div>;

  return (
    <div className="max-w-6xl mx-auto space-y-4 lg:space-y-6 pb-12 h-full flex flex-col min-w-0">
       <div className="shrink-0 flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4 mb-2">
         <div>
           <h1 className={`text-2xl lg:text-3xl font-black ${themeClasses.textHead} flex items-center gap-3`}><Calculator className="text-indigo-500" size={24}/> Sandbox</h1>
         </div>
       </div>

       <div className={`${themeClasses.bgCard} rounded-3xl border ${themeClasses.border} shadow-xl flex-1 flex flex-col overflow-hidden min-w-0`}>
          
          <div className={`p-4 border-b ${themeClasses.border} ${themeClasses.input} flex flex-col lg:flex-row justify-between items-center gap-4 shrink-0`}>
             <div className="flex items-center gap-4 w-full lg:w-auto">
               <span className={`text-[10px] font-black ${themeClasses.textMuted} uppercase tracking-widest hidden sm:block`}>Budget</span>
               <div className="relative flex-1 sm:flex-none">
                 <span className="absolute left-3 top-2.5 text-emerald-500 font-bold">$</span>
                 <input 
                   type="text" 
                   value={Math.round(budget || 0).toLocaleString()} 
                   onChange={(e) => handleBudgetChange(e.target.value.replace(/[^0-9.-]+/g, ''))}
                   className={`pl-7 pr-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 font-mono font-bold outline-none focus:border-emerald-500 text-sm sm:text-base w-full sm:w-40`}
                 />
               </div>
             </div>
             
             <div className="flex items-center gap-2 bg-slate-900/50 p-1.5 rounded-2xl border border-slate-700/50 overflow-x-auto w-full lg:w-auto custom-scrollbar">
               {Object.entries(SCENARIOS).map(([key, config]) => {
                 const Icon = config.icon;
                 const isActive = activeScenario === key;
                 return (
                   <div key={key} className={`flex items-center transition-all rounded-xl shrink-0 ${isActive ? config.bg + ' ring-1 ring-current ' + config.color : ''}`}>
                     <button 
                       onClick={() => setActiveScenario(key)}
                       className={`flex items-center gap-2 px-3 lg:px-4 py-2 text-[10px] font-black uppercase tracking-widest ${isActive ? '' : `text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded-xl`}`}
                     >
                       <Icon size={14}/> {config.label}
                     </button>
                     
                     {key === 'CUSTOM' && isActive && (
                        <div className="flex items-center mr-2 ml-1 bg-black/30 rounded-lg px-2 py-0.5 border border-indigo-500/30">
                          <input 
                            type="number" 
                            value={customShift} 
                            onChange={(e) => setCustomShift(e.target.value)}
                            className="w-12 bg-transparent text-indigo-400 outline-none text-right font-bold text-xs"
                          />
                          <span className="text-indigo-400 font-bold ml-1 text-[10px]">%</span>
                        </div>
                     )}
                   </div>
                 )
               })}
             </div>
          </div>
          
          <div className="flex-1 overflow-auto custom-scrollbar p-0 relative min-w-0">
              
              {isStressed && (
                <div className="sticky top-0 left-0 right-0 p-2 z-20 flex justify-center pointer-events-none min-w-max">
                  <div className={`px-6 py-2 rounded-full ${currentScenario.bg} border border-current text-[10px] font-black uppercase tracking-widest ${currentScenario.color} shadow-lg flex items-center gap-2 animate-pulse`}>
                    <AlertTriangle size={14}/> 1-YEAR BETA-WEIGHTED STRESS TEST ACTIVE
                  </div>
                </div>
              )}

              <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className={`sticky top-0 ${themeClasses.bgCard} z-10 shadow-sm`}>
                    <tr className={`${themeClasses.textMuted} uppercase text-[10px] tracking-widest border-b ${themeClasses.border}`}>
                      <th className="px-2 py-3 font-black">Symbol</th>
                      <th className="px-2 py-3 font-black">Beta (1Y) <span className="text-[8px] opacity-50 cursor-help" title="Volatility. 1.0 is market avg.">(?)</span></th>
                      <th className="px-2 py-3 font-black">{isStressed ? 'Stressed Price' : 'Price ($)'}</th>
                      <th className="px-2 py-3 font-black">Shares</th>
                      <th className="px-2 py-3 font-black">Weight (%)</th>
                      <th className="px-2 py-3 font-black">{isStressed ? 'Stressed Val ($)' : 'Allocated ($)'}</th>
                      <th className="px-2 py-3 font-black">{isStressed ? 'Shock' : 'Yield (%)'}</th>
                      <th className="px-2 py-3 font-black">{isStressed ? '' : 'Income ($)'}</th>
                      <th className="px-2 py-3 font-black text-center"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedRows.map((row) => (
                      <tr key={row.id} className={`border-b ${themeClasses.border} transition-colors ${isStressed ? (row.assetShiftPct < 0 ? 'bg-red-500/5' : 'bg-emerald-500/5') : 'hover:bg-indigo-500/5'}`}>
                        <td className="px-2 py-3">
                          <input 
                            value={row.symbol} 
                            onChange={(e) => updateRow(row.id, 'symbol', e.target.value.toUpperCase())}
                            onBlur={(e) => fetchAssetData(row.id, e.target.value)}
                            placeholder="TICKER" 
                            className={`w-16 bg-transparent border-b ${themeClasses.border} focus:border-indigo-500 outline-none font-bold ${themeClasses.textHead} placeholder-slate-600`} 
                          />
                        </td>
                        <td className="px-2 py-3">
                          <input type="number" value={row.beta} onChange={(e) => updateRow(row.id, 'beta', e.target.value)} className={`w-12 bg-transparent border-b ${themeClasses.border} focus:border-indigo-500 outline-none font-mono ${themeClasses.textMuted}`} />
                        </td>
                        <td className="px-2 py-3">
                          {!isStressed ? (
                            <input type="number" value={row.price} onChange={(e) => updateRow(row.id, 'price', e.target.value)} className={`w-16 bg-transparent border-b ${themeClasses.border} focus:border-indigo-500 outline-none font-mono ${themeClasses.textHead}`} />
                          ) : (
                            <span className={`font-mono font-bold ${row.assetShiftPct < 0 ? 'text-red-400' : 'text-emerald-400'}`}>${row.stressedPrice.toFixed(2)}</span>
                          )}
                        </td>
                        <td className="px-2 py-3">
                          <input type="number" disabled={isStressed} value={row.shares} onChange={(e) => updateRow(row.id, 'shares', e.target.value)} className={`w-20 bg-transparent border-b ${themeClasses.border} focus:border-indigo-500 outline-none font-mono ${themeClasses.textHead} ${isStressed && 'opacity-50'}`} />
                        </td>
                        <td className="px-2 py-3">
                          <input type="number" step="0.1" disabled={isStressed} value={row.weight} onChange={(e) => updateRow(row.id, 'weight', e.target.value)} className={`w-14 bg-transparent border-b ${themeClasses.border} focus:border-indigo-500 outline-none font-mono text-indigo-400 font-bold ${isStressed && 'opacity-50'}`} />
                        </td>
                        <td className="px-2 py-3">
                          {!isStressed ? (
                            <input 
                              type="text" 
                              value={`$${Math.round(row.budgetDollars || 0).toLocaleString()}`} 
                              onChange={(e) => updateRow(row.id, 'budgetDollars', e.target.value.replace(/[^0-9.-]+/g, ''))} 
                              className={`w-24 bg-transparent border-b ${themeClasses.border} focus:border-indigo-500 outline-none font-mono text-indigo-400 font-bold`} 
                            />
                          ) : (
                            <span className={`font-mono font-bold ${row.assetShiftPct < 0 ? 'text-red-400' : 'text-emerald-400'}`}>${Math.round(row.stressedAllocated || 0).toLocaleString()}</span>
                          )}
                        </td>
                        <td className="px-2 py-3">
                          {!isStressed ? (
                             <input type="number" value={row.yieldPct} onChange={(e) => updateRow(row.id, 'yieldPct', e.target.value)} className={`w-14 bg-transparent border-b ${themeClasses.border} focus:border-indigo-500 outline-none font-mono text-emerald-400 font-bold`} />
                          ) : (
                             <span className={`font-mono font-bold ${row.assetShiftPct < 0 ? 'text-red-500' : 'text-emerald-500'}`}>{row.assetShiftPct > 0 ? '+' : ''}{row.assetShiftPct.toFixed(2)}%</span>
                          )}
                        </td>
                        <td className={`px-2 py-3 font-mono font-bold text-emerald-500`}>
                          {!isStressed && `$${Math.round(row.yieldDollars || 0).toLocaleString()}`}
                        </td>
                        <td className="px-2 py-3 text-center">
                          <button onClick={() => removeRow(row.id)} className="text-red-500/50 hover:text-red-500 transition-colors p-1"><Trash2 size={14}/></button>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                         <td colSpan="9" className={`p-16 text-center ${themeClasses.textMuted} font-black uppercase tracking-widest text-xs`}>No assets in simulator. Add a row to begin.</td>
                      </tr>
                    )}
                  </tbody>
              </table>
          </div>
          
          <div className={`p-4 border-t ${themeClasses.border} ${themeClasses.input} flex flex-row justify-between items-center w-full shrink-0 transition-colors ${isStressed && currentScenario.bg}`}>
             {!isStressed ? (
               <div className="flex flex-row justify-between w-full items-center gap-4">
                 <div>
                   <span className={`text-[9px] font-black ${themeClasses.textMuted} uppercase tracking-widest block mb-1`}>Total Allocation</span>
                   <span className={`text-base lg:text-lg font-mono font-black ${totalWeight > 100.1 ? 'text-red-500' : themeClasses.textHead}`}>
                     ${Math.round(totalAllocated).toLocaleString()} <span className="text-[10px] lg:text-xs opacity-50">({totalWeight.toFixed(1)}%)</span>
                   </span>
                 </div>
                 <div className="text-center">
                   <span className={`text-[9px] font-black ${themeClasses.textMuted} uppercase tracking-widest block mb-1`}>Blended Yield</span>
                   <span className={`text-base lg:text-lg font-mono font-black text-emerald-500`}>{blendedYield.toFixed(2)}%</span>
                 </div>
                 <div className="text-right">
                   <span className={`text-[9px] font-black ${themeClasses.textMuted} uppercase tracking-widest block mb-1`}>Total Annual Income</span>
                   <span className={`text-base lg:text-lg font-mono font-black text-emerald-500`}>${Math.round(totalYieldDollars).toLocaleString()}</span>
                 </div>
               </div>
             ) : (
               <div className="flex flex-row justify-between w-full items-center gap-4">
                 <div>
                   <span className={`text-[9px] font-black ${currentScenario.color} opacity-70 uppercase tracking-widest block mb-1 hidden sm:block`}>Base Value</span>
                   <span className={`text-base lg:text-lg font-mono font-black ${themeClasses.textMuted} hidden sm:block`}>
                     ${Math.round(totalAllocated).toLocaleString()}
                   </span>
                 </div>
                 <div className="text-center">
                   <span className={`text-[9px] font-black ${currentScenario.color} uppercase tracking-widest block mb-1`}>Simulated Market Impact</span>
                   <span className={`text-base lg:text-lg font-mono font-black ${portfolioDrawdown < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                     {portfolioDrawdown > 0 ? '+' : ''}{portfolioDrawdown.toFixed(2)}% 
                     <span className="text-[10px] lg:text-xs ml-1 sm:ml-2">({portfolioDrawdown > 0 ? '+' : ''}${Math.round(totalStressedAllocated - totalAllocated).toLocaleString()})</span>
                   </span>
                 </div>
                 <div className="text-right">
                   <span className={`text-[9px] font-black ${currentScenario.color} uppercase tracking-widest block mb-1`}>New Stressed Value</span>
                   <span className={`text-base lg:text-lg font-mono font-black ${currentScenario.color}`}>
                     ${Math.round(totalStressedAllocated).toLocaleString()}
                   </span>
                 </div>
               </div>
             )}
          </div>

       </div>

       <div className="shrink-0 flex justify-end">
           <button onClick={addRow} className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 transition-colors">
             <Plus size={16}/> Add Asset
           </button>
       </div>
    </div>
  );
}